import {
	Dialog,
	Form,
	FormItem,
	RadioButtonGroup,
	RadioButtonGroupItem,
	renderReactDialog,
	Toolbar
} from "client/Components";
import { useDataInputDialog } from "client/Hooks";
import { DocumentEvent, publish } from "client_legacy/Documents/events";
import {
	getUserEditorPreference,
	setUserEditorPreference,
	UserEditorPreference
} from "client_legacy/Documents/officeDocumentsOpenPreference";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useState
} from "react";

export type SetOfficePreferenceDialogProps = {
	onClose: () => void;
};

export const SetOfficePreferenceDialog = (props: SetOfficePreferenceDialogProps): ReactElement => {
	const {
		onClose
	} = props;

	const [value, setValue] = useState<UserEditorPreference>(() => getUserEditorPreference());

	const setHandler = useCallback(
		(): void => {
			setUserEditorPreference(value);
			publish(DocumentEvent.PreferenceChanged, undefined);
			onClose();
		},
		[value, onClose]
	);

	return (
		<Dialog trackingId="office-editor-preference-dialog">
			<Dialog.Header
				title={localize("_SetPreferenceForOpeningOfficeDocuments")}
				onClose={onClose}
			/>

			<Dialog.Body>
				<Form onSubmit={setHandler}>
					<FormItem isForGroup>
						<RadioButtonGroup
							value={value}
							onChange={setValue}
							trackingId="preference"
							options={preferenceOptions}
						/>
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_SetPreference")}
						trackingId="set-preference"
						onClick={setHandler}
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Cancel")}
						trackingId="cancel"
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
SetOfficePreferenceDialog.displayName = "SetOfficePreferenceDialog";

const preferenceOptions: readonly RadioButtonGroupItem<UserEditorPreference>[] = [
	{ text: localize("_NotSpecified") , value: UserEditorPreference.None },
	{ text: localize("_OpenInOfficeOnline") , value: UserEditorPreference.Online },
	{ text: localize("_OpenInOfficeDesktopApp") , value: UserEditorPreference.Local }
];

export function useSetOfficePreferenceDialog(): [
	element: ReactElement | null,
	open: () => Promise<void>
] {
	const [element, originalOpen] = useDataInputDialog<{}, void>(
		(_props, onClose) => (
			<SetOfficePreferenceDialog
				onClose={onClose}
			/>
		)
	);

	const open = useCallback(
		async (): Promise<void> => {
			await originalOpen({});
		},
		[originalOpen]
	);

	return [element, open];
}

export function openSetOfficePreferenceDialog(): Promise<void> {
	return new Promise<void>(
		(resolve) => {
			renderReactDialog(
				(onClose) => (
					<SetOfficePreferenceDialog
						onClose={() => {
							onClose();
							resolve();
						}}
					/>
				)
			);
		}
	);
}
